import {
  Component,
  ChangeDetectionStrategy,
  AfterViewChecked,
  inject,
  DestroyRef,
  ViewChild,
  ElementRef,
  HostListener,
  computed,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { SharelockCommonModule } from '../../../../share/modules/sharelock-common.module';
import { NavigationService } from '../../../services/navigation.service';
import { AccountButtonsComponent } from '../components/account-buttons/account-buttons.component';
import { LanguageButtonComponent } from '../components/language-button/language-button.component';
import { TopBarNavigationButtonsComponent } from './navigation-buttons/top-bar-navigation-buttons.component';
import { ENavigationRoute } from '../../../model/navigation.model';
import { AccountDataService } from '../../../services/auth/account-data.service';
import { filter } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  AnimationStateE,
  SharelockAnimations,
} from '../../../../share/utils/animations';

@Component({
  selector: 'shrl-top-bar',
  imports: [
    SharelockCommonModule,
    MatButtonModule,
    MatIconModule,
    TopBarNavigationButtonsComponent,
    LanguageButtonComponent,
    MatRippleModule,
    AccountButtonsComponent,
  ],
  animations: [SharelockAnimations.height],
  templateUrl: './top-bar.component.html',
  styleUrl: './top-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopBarComponent implements AfterViewChecked {
  readonly #destroyRef = inject(DestroyRef);
  readonly #accountDataService = inject(AccountDataService);
  readonly #translate = inject(TranslateService);
  readonly navigationService = inject(NavigationService);

  @ViewChild('emptySpaceCointainer') emptySpaceCointainer?: ElementRef;
  @ViewChild('navigationButtonsContainer')
  navigationButtonsContainer?: ElementRef;
  @ViewChild('profileButtonsContainer')
  profileButtonsContainer?: ElementRef;

  ENavigationRoute = ENavigationRoute;

  #maxNavigationButtonsSpace = 0;
  #maxProfileButtonsContainerSpace = 0;
  #spacesToReset = false;

  readonly topbarAnimationState = computed(() =>
    this.navigationService.isTopbarOpened()
      ? AnimationStateE.SHOW
      : AnimationStateE.HIDE
  );

  constructor() {
    this.#setAccountDataServiceSubscribers();
    this.#setLangChangeSubscriber();
  }

  @HostListener('window:resize')
  onResize() {}

  ngAfterViewChecked() {
    this.#setContainerSpaceAfterViewChecked();
    this.#setSidenavVisibility();
    this.#setProfileButtonsVisibility();
  }

  sidenavToggle() {
    this.navigationService.sidenavToggle();
  }

  readonly leftContainerClass = computed(() =>
    this.navigationService.isSidenavActive() ? 'pl-2' : 'pl-2 lg:pl-4'
  );

  #setContainerSpaceAfterViewChecked() {
    if (this.#spacesToReset) {
      this.#resetmaxNavigationButtonsSpace();
    } else {
      this.#setMaxNavigationButtonsSpace();
      this.#setMaxProfileButtonsContainerSpace();
    }
    this.#spacesToReset = false;
  }

  #setAccountDataServiceSubscribers() {
    this.#accountDataService.isLoggedIn$
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((isLogged) => !isLogged)
      )
      .subscribe(() => (this.#spacesToReset = true));
  }

  #setLangChangeSubscriber() {
    this.#translate.onFallbackLangChange
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => (this.#spacesToReset = true));
  }

  #setSidenavVisibility() {
    if (this.#totalSpace == null) {
      return;
    }
    if (
      this.#totalSpace <=
      this.#maxNavigationButtonsSpace + this.#maxProfileButtonsContainerSpace
    ) {
      this.navigationService.setSideNavActive(true);
    } else {
      this.navigationService.setSideNavActive(false);
      this.navigationService.setSidenavOpened(false);
    }
  }

  #setProfileButtonsVisibility() {
    if (this.#totalSpace == null) {
      return;
    }
    if (this.#totalSpace > this.#maxProfileButtonsContainerSpace) {
      this.navigationService.setProfileButtonsVisible(true);
    } else {
      this.navigationService.setProfileButtonsVisible(false);
    }
  }

  #setMaxNavigationButtonsSpace() {
    if (this.#navigationButtonsContainerSpace == null) {
      return;
    }
    if (
      this.#navigationButtonsContainerSpace > this.#maxNavigationButtonsSpace
    ) {
      this.#maxNavigationButtonsSpace = this.#navigationButtonsContainerSpace;
    }
  }

  #setMaxProfileButtonsContainerSpace() {
    if (this.#profileButtonsContainerSpace == null) {
      return;
    }
    if (
      this.#profileButtonsContainerSpace > this.#maxProfileButtonsContainerSpace
    ) {
      this.#maxProfileButtonsContainerSpace =
        this.#profileButtonsContainerSpace;
    }
  }

  #resetmaxNavigationButtonsSpace = () => {
    this.#maxNavigationButtonsSpace = 0;
    this.#maxProfileButtonsContainerSpace = 0;
    this.#setMaxNavigationButtonsSpace();
    this.#setMaxProfileButtonsContainerSpace();
  };

  // Total space without logo and external paddings
  get #totalSpace(): number | undefined {
    if (
      this.#emptySpace == null ||
      this.#navigationButtonsContainerSpace == null ||
      this.#profileButtonsContainerSpace == null
    ) {
      return undefined;
    }
    return (
      this.#emptySpace +
      this.#navigationButtonsContainerSpace +
      this.#profileButtonsContainerSpace
    );
  }

  get #emptySpace(): number | undefined {
    return this.emptySpaceCointainer?.nativeElement?.offsetWidth;
  }

  get #navigationButtonsContainerSpace(): number | undefined {
    return this.navigationButtonsContainer?.nativeElement?.offsetWidth;
  }

  get #profileButtonsContainerSpace(): number | undefined {
    return this.profileButtonsContainer?.nativeElement?.offsetWidth;
  }
}
